import { registerCommand } from "./registry.ts";
import {
  moveStatement,
  copyStatement,
  deleteStatement,
  insertAfter,
  createStatement,
  transposeStatements,
} from "../../documents/tree.ts";
import { generateSIDs } from "../../documents/addressing.ts";
import { documentToDoc } from "../state.ts";
import type { CommandContext } from "./registry.ts";

/**
 * Reload the editor with updated document tree.
 */
function reloadEditor(ctx: CommandContext): void {
  if (!ctx.editorView || !ctx.currentDocument) return;
  const newDoc = documentToDoc(ctx.currentDocument.root);
  const tr = ctx.editorView.state.tr.replaceWith(
    0,
    ctx.editorView.state.doc.content.size,
    newDoc.content
  );
  ctx.editorView.dispatch(tr);
}

// --- Move ---
registerCommand({
  name: "move",
  aliases: ["m", "ms"],
  description: "Move statement to new location",
  args: [
    { name: "source", type: "sid", prompt: "Move:" },
    { name: "destination", type: "sid", prompt: "To after:" },
  ],
  execute(ctx, args) {
    if (!ctx.currentDocument || !args.source || !args.destination) return;
    if (moveStatement(ctx.currentDocument.root, args.source, args.destination)) {
      reloadEditor(ctx);
    }
  },
});

// --- Copy ---
registerCommand({
  name: "copy",
  aliases: ["c", "cs"],
  description: "Copy statement to new location",
  args: [
    { name: "source", type: "sid", prompt: "Copy:" },
    { name: "destination", type: "sid", prompt: "To after:" },
  ],
  execute(ctx, args) {
    if (!ctx.currentDocument || !args.source || !args.destination) return;
    if (
      copyStatement(ctx.currentDocument.root, args.source, args.destination)
    ) {
      reloadEditor(ctx);
    }
  },
});

// --- Delete ---
registerCommand({
  name: "delete",
  aliases: ["d", "ds"],
  description: "Delete statement",
  args: [
    { name: "target", type: "sid", prompt: "Delete:" },
    { name: "confirm", type: "confirm", prompt: "Confirm (y/n):" },
  ],
  execute(ctx, args) {
    if (!ctx.currentDocument || !args.target) return;
    if (args.confirm !== "y" && args.confirm !== "yes") return;
    if (deleteStatement(ctx.currentDocument.root, args.target)) {
      reloadEditor(ctx);
    }
  },
});

// --- Insert ---
registerCommand({
  name: "insert",
  aliases: ["is"],
  description: "Insert new statement after target",
  args: [{ name: "after", type: "sid", prompt: "Insert after:" }],
  execute(ctx, args) {
    if (!ctx.currentDocument || !args.after) return;
    const newStmt = createStatement("");
    if (insertAfter(ctx.currentDocument.root, args.after, newStmt)) {
      reloadEditor(ctx);
    }
  },
});

// --- Transpose ---
registerCommand({
  name: "transpose",
  aliases: ["t", "ts"],
  description: "Swap two statements",
  args: [
    { name: "a", type: "sid", prompt: "Swap:" },
    { name: "b", type: "sid", prompt: "With:" },
  ],
  execute(ctx, args) {
    if (!ctx.currentDocument || !args.a || !args.b) return;
    if (transposeStatements(ctx.currentDocument.root, args.a, args.b)) {
      reloadEditor(ctx);
    }
  },
});
